import { useState, useEffect } from "react";

/**
 * Drop-in useState replacement backed by sessionStorage — survives a page refresh and
 * navigating away to another tab and back, but clears once the browser tab/window closes.
 * Same idiom as useCRMSort's localStorage persistence, just session-scoped.
 *
 * `key` should be unique per usage site (e.g. "clients:selectedId") to avoid collisions.
 */
export function useSessionState<T>(key: string, defaultValue: T) {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // sessionStorage full/unavailable (private browsing, etc.) — memory-only fallback
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, state]);

  return [state, setState] as const;
}

// For sections where "where the user was" already lives in the URL (Notes' /notes/:id,
// Files' /files?folder=) rather than component state — remember the last full path so the
// sidebar nav link can return there, instead of each page persisting its own local state.
const sectionPathKey = (section: string) => `lastPath:${section}`;

export function rememberSectionPath(section: string, path: string): void {
  try {
    sessionStorage.setItem(sectionPathKey(section), path);
  } catch {
    // sessionStorage full/unavailable — silently skip, falls back to the default path
  }
}

/**
 * Forget a section's remembered path.
 *
 * Needed when the user deliberately leaves a sub-tab (the in-page back button):
 * MeetingContainer resumes into the remembered path on landing at the client
 * root, so leaving without clearing it bounces straight back to the tab just
 * exited. The navigation-type guard there only covers browser back (a POP) —
 * an in-app navigate() is a PUSH and has to say so explicitly.
 */
export function forgetSectionPath(section: string): void {
  try {
    sessionStorage.removeItem(sectionPathKey(section));
  } catch {
    // sessionStorage unavailable — the resume simply falls back to the default path
  }
}

export function getLastSectionPath(section: string, fallback: string): string {
  try {
    return sessionStorage.getItem(sectionPathKey(section)) || fallback;
  } catch {
    return fallback;
  }
}

// The in-progress email draft the user was last composing — a pointer (draft id), not the
// content itself, since the content is already durably saved server-side via saveDraft().
// EmailPage reads this on mount to reopen ComposePanel on that draft instead of the inbox.
// Scoped per clientId when composing from a client's own Email tab, so drafting in one
// client's tab doesn't reopen in a different client's tab (or the generic inbox).
const ACTIVE_EMAIL_DRAFT_KEY = "email:activeDraftId";
const activeEmailDraftKey = (clientId?: string) =>
  clientId ? `email:activeDraftId:client:${clientId}` : ACTIVE_EMAIL_DRAFT_KEY;

export function rememberActiveEmailDraft(draftId: string | null, clientId?: string): void {
  try {
    const key = activeEmailDraftKey(clientId);
    if (draftId) {
      sessionStorage.setItem(key, draftId);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // sessionStorage full/unavailable — silently skip
  }
}

export function getActiveEmailDraftId(clientId?: string): string | null {
  try {
    return sessionStorage.getItem(activeEmailDraftKey(clientId));
  } catch {
    return null;
  }
}
